import { accessTokenStore } from "./access-token"
import {
  API_BASE_URL,
  REFRESH_RACE_RETRY_MAX_MS,
  REFRESH_RACE_RETRY_MIN_MS,
  REQUEST_TIMEOUT_MS,
} from "./config"
import { noteResponseSource, readCachedAt } from "../offline/register"
import { ApiProblem, isApiProblem, problemFromBody, transportProblem } from "./problem"

/**
 * Единственная дверь кабинета наружу.
 *
 * Здесь собраны три вещи, которые нельзя размазывать по экранам:
 * 1. Заголовок Authorization и молчаливое обновление сессии.
 * 2. Разбор отказа в ApiProblem — чтобы экран получал код, а не текст.
 * 3. Три исхода обновления сессии, которые путать нельзя (см. refreshSession).
 */

export type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE"

export type QueryValue = string | number | boolean | readonly string[] | null | undefined

/**
 * Необязательные поля объявлены с явным `| undefined`: в проекте включён
 * exactOptionalPropertyTypes, и без этого нельзя передать `{ signal }`, где signal
 * пришёл сверху необязательным, — а это самый обычный вызов.
 */
export interface RequestOptions {
  readonly method?: HttpMethod | undefined
  readonly body?: unknown
  readonly query?: Readonly<Record<string, QueryValue>> | undefined
  readonly signal?: AbortSignal | undefined
  /**
   * Слать ли Authorization и обновлять ли сессию молча. Выключается ровно на трёх
   * запросах контура входа: login, mfa/verify и сам refresh.
   */
  readonly authenticated?: boolean | undefined

  /**
   * Ключ идемпотентности. ВСЕ мутации кабинета его требуют: без заголовка сервер
   * отвечает 400 с type .../idempotency-key-required, при кривом значении —
   * .../idempotency-key-invalid (8–255 символов).
   *
   * Поле именованное, а не «произвольные заголовки»: произвольный словарь заголовков
   * в единственной двери наружу — это приглашение отправить Authorization мимо
   * accessTokenStore и мимо молчаливого обновления сессии.
   *
   * Ключ приходит СВЕРХУ и переживает внутренний повтор после обновления сессии:
   * иначе повтор превратился бы в второй объект, а смысл ключа — ровно обратный.
   */
  readonly idempotencyKey?: string | undefined
}

/** Почему сессия закончилась. От причины зависит, что показать человеку. */
export type SessionLostReason =
  /** Обычное протухание или выход: просто экран входа. */
  | "expired"
  /**
   * Предъявлен уже отработавший refresh — признак кражи. Сервер оборвал всё семейство
   * сессий, и кабинет обязан не просто увести на вход, а предложить сменить пароль.
   */
  | "reuse_detected"

type SessionLostHandler = (reason: SessionLostReason) => void

const sessionLostHandlers = new Set<SessionLostHandler>()

/** Подписка на «сессии больше нет». Вызывается провайдером доступа, а не экранами. */
export function onSessionLost(handler: SessionLostHandler): () => void {
  sessionLostHandlers.add(handler)
  return () => {
    sessionLostHandlers.delete(handler)
  }
}

const tenantAccessHandlers = new Set<(problem: ApiProblem) => void>()

/**
 * Подписка на «салону закрыли доступ, пока человек работал».
 *
 * ЗАЧЕМ ОТДЕЛЬНОЕ СОБЫТИЕ, А НЕ ЭКРАН ОШИБКИ НА КАЖДОМ ЗАПРОСЕ. Ограничение приходит
 * не на входе, а посреди рабочего дня: администратор нажимает «Записать», и сервер
 * отвечает 403. Показать это одной ошибкой в диалоге значит оставить человека в кабинете,
 * где не работает уже ничто, и он будет нажимать дальше. Кабинет обязан перечитать
 * состояние салона и показать экран ограничения целиком — один раз и на весь кабинет.
 */
export function onTenantAccessLost(handler: (problem: ApiProblem) => void): () => void {
  tenantAccessHandlers.add(handler)
  return () => {
    tenantAccessHandlers.delete(handler)
  }
}

function notifyTenantAccessLost(problem: ApiProblem): void {
  for (const handler of tenantAccessHandlers) handler(problem)
}

function notifySessionLost(reason: SessionLostReason): void {
  for (const handler of sessionLostHandlers) handler(reason)
}

/**
 * Сессия заведомо мертва: повторные попытки обновления бессмысленны и только шумят
 * в журнале сервера. Снимается успешным входом.
 */
let sessionKnownDead = false

/** Обновление сессии идёт в одном экземпляре: иначе пять параллельных запросов после
 * протухания токена устроят пять ротаций refresh и сами себе организуют «кражу». */
let refreshInFlight: Promise<void> | null = null

function buildUrl(path: string, query?: Readonly<Record<string, QueryValue>>): string {
  const base = `${API_BASE_URL}${path}`
  if (query === undefined) return base

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue
    if (Array.isArray(value)) {
      // Контроллер журнала принимает и повторяющийся параметр, и список через запятую.
      // Повторяющийся честнее: запятая в идентификаторе не встречается, но и не запрещена.
      for (const item of value) params.append(key, item)
      continue
    }
    params.append(key, String(value))
  }

  const qs = params.toString()
  return qs.length > 0 ? `${base}?${qs}` : base
}

function buildSignal(external: AbortSignal | undefined): AbortSignal {
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT_MS)
  if (external === undefined) return timeout
  if (typeof AbortSignal.any === "function") return AbortSignal.any([external, timeout])
  return external
}

async function readBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") ?? ""
  if (response.status === 204 || response.headers.get("content-length") === "0") return null
  if (!contentType.includes("json")) {
    // Не JSON — это nginx с его HTML-страницей ошибки или упавший процесс.
    // Текст наружу не показываем: в нём ничего полезного человеку нет.
    await response.text().catch(() => "")
    return null
  }
  try {
    return (await response.json()) as unknown
  } catch {
    return null
  }
}

function toProblem(error: unknown): ApiProblem {
  if (isApiProblem(error)) return error
  if (error instanceof DOMException && error.name === "TimeoutError") {
    return transportProblem("timeout")
  }
  if (error instanceof DOMException && error.name === "AbortError") {
    return transportProblem("aborted")
  }
  // fetch падает TypeError на любой сетевой беде: обрыв, DNS, отказ TLS.
  // Для человека это одно и то же — «нет связи», и полоса состояния скажет именно так.
  return transportProblem("offline")
}

/** Запрос без всякой логики сессии. На нём же строится сам refresh. */
async function rawRequest<T>(path: string, options: RequestOptions): Promise<T> {
  const headers = new Headers({ Accept: "application/json" })

  if (options.body !== undefined) headers.set("Content-Type", "application/json")
  if (options.idempotencyKey !== undefined) {
    headers.set("Idempotency-Key", options.idempotencyKey)
  }

  if (options.authenticated !== false) {
    const token = accessTokenStore.read()
    if (token !== null) headers.set("Authorization", `Bearer ${token}`)
  }

  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? "GET",
      headers,
      // Cookie с refresh уходит сама и только на свой источник. Кабинет и API обязаны
      // быть одним источником — на чужой SameSite=Strict её не пустит.
      credentials: "same-origin",
      body: options.body === undefined ? null : JSON.stringify(options.body),
      signal: buildSignal(options.signal),
    })
  } catch (error) {
    throw toProblem(error)
  }

  const body = await readBody(response)

  /*
   * ОТКУДА ПРИШЁЛ ОТВЕТ — ЭТО ФАКТ ДЛЯ ЧЕЛОВЕКА, А НЕ ПОДРОБНОСТЬ ТРАНСПОРТА.
   *
   * Без связи service worker отдаёт сохранённый ответ и ставит на него отметку времени
   * (offline/cache-policy.ts). Экран обязан её показать: «Показываю расписание,
   * загруженное сегодня в 09:15» — это единственное, что отличает рабочий экран
   * от вранья (ТЗ 05 §5.14). Отмечается ЗДЕСЬ, потому что заголовки видит только этот
   * файл: наружу он отдаёт разобранное тело.
   *
   * Отметка снимается на каждом сетевом ответе — иначе журнал, один раз прочитанный
   * из кеша, до конца сеанса писал бы «показываю сохранённое» уже при живой связи.
   */
  if ((options.method ?? 'GET') === 'GET') {
    noteResponseSource(path, readCachedAt(response.headers))
  }

  if (!response.ok) throw problemFromBody(response.status, body)

  return body as T
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

function raceRetryDelay(): number {
  const span = REFRESH_RACE_RETRY_MAX_MS - REFRESH_RACE_RETRY_MIN_MS
  return REFRESH_RACE_RETRY_MIN_MS + Math.floor(Math.random() * span)
}

interface TokenPairResponse {
  readonly accessToken: string
  readonly expiresAt: string
  readonly refreshToken: string | null
}

async function callRefresh(): Promise<void> {
  const pair = await rawRequest<TokenPairResponse>("/cabinet/auth/refresh", {
    method: "POST",
    // Тело пустое намеренно: в браузере refresh приезжает cookie. Поле refreshToken
    // в контракте есть, но оно для нативного приложения персонала, у которого cookie нет.
    body: {},
    authenticated: false,
  })
  accessTokenStore.write(pair.accessToken, pair.expiresAt)
}

/**
 * Обновление сессии. ТРИ ИСХОДА, КОТОРЫЕ ПУТАТЬ НЕЛЬЗЯ:
 *
 * 200 — работаем дальше.
 * 409 session_refresh_raced — ту же пару уже обновила соседняя вкладка. Cookie цела,
 *   в ней лежит новая пара; надо просто повторить запрос один раз через 200–500 мс.
 *   Показать здесь экран входа — значит разлогинить салон нажатием F5 в соседней вкладке.
 * 401 session_reuse_detected — предъявлен отработавший токен, семейство сессий оборвано.
 *   Это признак кражи: вход заново и предложение сменить пароль.
 */
async function performRefresh(): Promise<void> {
  try {
    await callRefresh()
    sessionKnownDead = false
    return
  } catch (error) {
    if (isApiProblem(error) && error.code === "session_refresh_raced") {
      await sleep(raceRetryDelay())
      await callRefresh()
      sessionKnownDead = false
      return
    }

    accessTokenStore.clear()
    if (isApiProblem(error) && error.sessionLost) {
      sessionKnownDead = true
      notifySessionLost(error.code === "session_reuse_detected" ? "reuse_detected" : "expired")
    }
    throw error
  }
}

function refreshSession(): Promise<void> {
  if (refreshInFlight !== null) return refreshInFlight
  const started = performRefresh()
  refreshInFlight = started.finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

/**
 * Поднять сессию после обновления страницы. Зовётся один раз на старте: access-токен
 * живёт в памяти вкладки и обновление её стирает, а cookie с refresh — нет.
 */
export async function restoreSession(): Promise<void> {
  sessionKnownDead = false
  await refreshSession()
}

/** Сессия начата заново (вход или выбор салона): прошлые отказы больше ничего не значат. */
export function markSessionAlive(): void {
  sessionKnownDead = false
}

export function forgetSession(): void {
  accessTokenStore.clear()
  sessionKnownDead = true
}

/**
 * Запрос к API. Если токена нет или сервер сказал, что он протух, сессия обновляется
 * молча и запрос повторяется РОВНО ОДИН раз — бесконечная карусель обновлений при
 * действительно закончившейся сессии хуже честного экрана входа.
 */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const authenticated = options.authenticated !== false

  if (authenticated && !sessionKnownDead && accessTokenStore.read() === null) {
    await refreshSession()
  }

  try {
    return await rawRequest<T>(path, options)
  } catch (error) {
    /*
     * Салон закрыли или доступ к нему отозвали, пока человек работал. Сообщаем один раз
     * на весь кабинет и передаём ПРИЧИНУ: «салон приостановлен» и «вас убрали из салона»
     * — разные новости и разные экраны, и различать их обязан не экран, а тот, кто знает
     * код ответа. Ошибку всё равно пробрасываем: экран, который её ждал, покажет свой
     * отказ и не останется в загрузке навсегда.
     */
    if (
      isApiProblem(error) &&
      (error.code === "tenant_suspended" || error.code === "tenant_access_revoked")
    ) {
      notifyTenantAccessLost(error)
    }

    const recoverable =
      authenticated &&
      !sessionKnownDead &&
      isApiProblem(error) &&
      error.status === 401 &&
      error.code === "session_invalid"

    if (!recoverable) throw error

    await refreshSession()
    return await rawRequest<T>(path, options)
  }
}
